import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { 
  FiLogOut,
  FiGrid,
  FiLogIn,
  FiUserPlus
} from 'react-icons/fi';
import { 
  LogoutConfirmationModal,
  Avatar,
  Button
} from '../common';
import { useAuth } from '../../hooks';
import { ROUTES } from '../../utils/constants';

const DashboardLayout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Check if current page is dashboard
  const isDashboardPage = location.pathname === ROUTES.DASHBOARD;

  // Set document title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'Dashboard';
    
    if (path.includes('/dashboard')) title = 'Dashboard';
    else if (path.includes('/profile')) title = 'Profile';
    else if (path.includes('/host-meeting')) title = 'Host Meeting';
    else if (path.includes('/join-meeting')) title = 'Join Meeting';
    else if (path.includes('/meeting-passes')) title = 'Meeting Passes';
    else if (path.includes('/reports')) title = 'Reports';
    else if (path.includes('/settings')) title = 'Settings';
    else if (path.includes('/help')) title = 'Help & Support';
    else if (path.includes('/p/')) title = 'Meeting Details';
    
    // Set the document title
    document.title = `SpeedMeet.ai | ${title}`;
  }, [location]);
  
  // Track scroll position for header blur effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle logout button click
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // Handle confirmed logout
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    logout();
  };

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 -z-10 bg-background-dark">
        <div className="absolute left-1/4 top-1/4 w-96 h-96 bg-primary-500/20 rounded-full filter blur-[100px]" />
        <div className="absolute right-1/4 bottom-1/4 w-96 h-96 bg-secondary-500/20 rounded-full filter blur-[100px]" />
      </div>
      
      {/* Fixed Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-6 py-4 transition-all duration-300 border-b border-white/10 ${
          scrolled ? 'shadow-lg' : ''
        }`}
        style={{
          backgroundColor: scrolled ? 'rgba(12, 12, 12, 0.75)' : 'rgba(12, 12, 12, 0.65)',
          backdropFilter: `blur(${scrolled ? '30px' : '10px'})`,
          WebkitBackdropFilter: `blur(${scrolled ? '30px' : '10px'})`,
        }}
      >
        {/* Logo - Updated to link to HOME instead of dashboard */}
        <div className="flex items-center">
          <Link to={ROUTES.HOME} className="flex items-center">
            <span className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
              SpeedMeet.ai
            </span>
          </Link>
        </div>
        
        <div className="flex items-center space-x-4">
          {isAuthenticated ? (
            <>
              {/* Dashboard Button - Only visible when not on dashboard page and user is authenticated */}
              {!isDashboardPage && (
                <Link to={ROUTES.DASHBOARD} className="flex items-center">
                  <Button 
                    variant="glass" 
                    size="sm" 
                    className="flex items-center justify-center h-10 px-4"
                    leftIcon={<FiGrid />}
                  >
                    <span className="hidden md:inline ml-1">Dashboard</span>
                  </Button>
                </Link>
              )}
              
              {/* Logout Button */}
              <div className="flex items-center justify-center">
                <button
                  onClick={handleLogoutClick}
                  className="flex items-center justify-center h-10 w-10 rounded-full hover:bg-white/5 transition-colors"
                >
                  <FiLogOut className="text-white/80 text-xl" />
                </button>
              </div>
              
              {/* User Avatar */}
              <div className="flex items-center h-10">
                <Avatar 
                  src={user?.profilePicture} 
                  name={user?.name}
                  size="sm" 
                  status="online"
                />
                <span className="ml-3 text-white hidden md:block">{user?.name}</span>
              </div>
            </>
          ) : (
            <>
              {/* Login Button - Only visible when user is not authenticated */}
              <Link to={ROUTES.LOGIN} className="flex items-center">
                <Button
                  variant="glass"
                  size="sm"
                  className="h-10 px-4 flex items-center justify-center"
                  leftIcon={<FiLogIn />}
                >
                  Login
                </Button>
              </Link>
              
              {/* Sign Up Button - Only visible when user is not authenticated */}
              <Link to={ROUTES.SIGNUP} className="flex items-center">
                <Button
                  variant="primary"
                  size="sm"
                  className="h-10 px-4 flex items-center justify-center"
                  glowEffect
                  leftIcon={<FiUserPlus />}
                >
                  Sign Up
                </Button>
              </Link>
            </>
          )}
        </div>
      </header>
      
      {/* Main Content - Add padding top to account for fixed header */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden p-6 pt-20 relative min-h-0">
        {/* Additional decorative elements */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary-500/10 rounded-full" />
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-secondary-500/10 rounded-full" />
        
        {/* Page content - FULL HEIGHT CONTAINER */}
        <div className="relative z-10 max-w-full min-h-full flex flex-col">
          <Outlet />
        </div>
      </main>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleConfirmLogout}
      />
    </div>
  );
};

export default DashboardLayout;
